<script lang="ts">
  import { onMount } from 'svelte';
  import { PASSWORD_MIN_LENGTH, type InvitePreviewResponse } from '@ucc/shared';
  import { ApiError, acceptInvite, fetchInvitePreview } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import PasswordField from '$lib/components/PasswordField.svelte';

  let { token }: { token: string } = $props();

  let loading = $state(true);
  let submitting = $state(false);
  let preview = $state<InvitePreviewResponse | null>(null);
  let error = $state<string | null>(null);
  let password = $state('');
  let confirm = $state('');

  const isReset = $derived(preview?.purpose === 'reset');
  const title = $derived(isReset ? 'Reset your password' : 'Activate your account');
  const leadVerb = $derived(isReset ? 'Choose a new password' : 'Set a password');
  const submitLabel = $derived(isReset ? 'Reset password' : 'Activate account');
  const failLabel = $derived(isReset ? 'Could not reset password' : 'Could not activate account');

  onMount(() => {
    void load();
  });

  async function load() {
    loading = true;
    error = null;
    try {
      preview = await fetchInvitePreview(token);
    } catch (err) {
      preview = null;
      error = err instanceof ApiError ? err.message : 'Invalid or expired link';
    } finally {
      loading = false;
    }
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (!preview) return;
    error = null;
    if (password.length < PASSWORD_MIN_LENGTH) {
      error = `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
      return;
    }
    if (password !== confirm) {
      error = 'Passwords do not match.';
      return;
    }
    submitting = true;
    try {
      const res = await acceptInvite({ token, password });
      session.user = res.user;
      session.status = 'authenticated';
      window.location.hash = '#/dashboard';
    } catch (err) {
      error = err instanceof ApiError ? err.message : failLabel;
    } finally {
      submitting = false;
    }
  }
</script>

<div class="wrap">
  <div class="panel">
    <p class="brand">Unarvu Cloud Connect</p>
    <h1>{title}</h1>

    {#if loading}
      <p class="muted">Checking link…</p>
    {:else if !preview}
      <p class="err" role="alert">{error ?? 'Link not available.'}</p>
      <p class="muted"><a href="#/login">Back to sign in</a></p>
    {:else}
      <p class="lead">
        {#if isReset}
          Hi <strong>{preview.displayName}</strong>. {leadVerb} for
          <strong>{preview.tenantName}</strong>
          <span class="role">({preview.role})</span>.
        {:else}
          Welcome, <strong>{preview.displayName}</strong>. Set a password to join
          <strong>{preview.tenantName}</strong>
          <span class="role">({preview.role})</span>.
        {/if}
      </p>
      <p class="meta">Invite for {preview.email}</p>
      <p class="meta">Expires {new Date(preview.expiresAt).toLocaleString()}.</p>

      <form class="form" onsubmit={(e) => void onSubmit(e)}>
        <div class="field">
          <span class="field-label">Password</span>
          <PasswordField
            bind:value={password}
            name="password"
            autocomplete="new-password"
            required
            minlength={PASSWORD_MIN_LENGTH}
            disabled={submitting}
          />
        </div>
        <div class="field">
          <span class="field-label">Confirm password</span>
          <PasswordField
            bind:value={confirm}
            name="confirm"
            autocomplete="new-password"
            required
            minlength={PASSWORD_MIN_LENGTH}
            disabled={submitting}
          />
        </div>
        {#if error}
          <p class="err" role="alert">{error}</p>
        {/if}
        <Button type="submit" disabled={submitting}>
          {submitting ? (isReset ? 'Saving…' : 'Activating…') : submitLabel}
        </Button>
      </form>
    {/if}
  </div>
</div>

<style>
  .wrap {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 2rem 1rem;
    background: linear-gradient(160deg, #0f172a 0%, #1e293b 45%, #334155 100%);
    color-scheme: light;
  }
  .panel {
    width: min(420px, 100%);
    background: #ffffff;
    border-radius: 16px;
    padding: 2rem 1.75rem;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
    color: #0f172a;
    color-scheme: light;
  }
  .brand {
    margin: 0 0 0.35rem;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #2563eb;
  }
  h1 {
    margin: 0 0 1rem;
    font-size: 1.45rem;
    color: #0f172a;
  }
  .lead {
    margin: 0 0 0.5rem;
    color: #334155;
    line-height: 1.45;
  }
  .role {
    text-transform: capitalize;
    color: #64748b;
  }
  .meta {
    margin: 0.15rem 0;
    font-size: 0.85rem;
    color: #64748b;
  }
  .form {
    display: grid;
    gap: 0.85rem;
    margin-top: 1.25rem;
  }
  .field {
    display: grid;
    gap: 0.35rem;
    font-size: 0.9rem;
    color: #334155;
  }

  .err {
    color: #b91c1c;
    margin: 0;
    font-size: 0.9rem;
  }
  .muted {
    color: #64748b;
  }
  a {
    color: #2563eb;
  }

  .field-label {
    display: block;
    margin-bottom: 0.3rem;
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.875rem;
  }
</style>
